import { Route, Routes, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DashboardAdmin from "../page/Admin/DashboardAdmin";
import Users from "../page/Admin/Users";
import UserForm from "../page/Admin/UserForm";
import SidebarAdmin from "../component/Admin/SidebarAdmin";
import NavbarAdmin from "../component/Admin/NavbarAdmin";
import Loa from "../page/Admin/Loa";
import LoaForm from "../page/Admin/LoaForm";
import LoaDetail from "../page/Admin/LoaDetail";
import Journal from "../page/Admin/Journal";
import Issues from "../page/Admin/Issues";
import Payment from "../page/Admin/Payment/Payment";
import PaymentDetail from "../page/Admin/Payment/PaymentDetail";
import PaymentBill from "../page/Admin/Payment/PaymentBill";
import PaymentMethod from "../page/Admin/Payment/PaymentMethod";
import PaymentInvoice from "../page/Admin/Payment/PaymentInvoice";
import MenuAccess from "../page/Admin/MenuAccess";
import MenuManage from "../page/Admin/MenuManage";
import Settings from "../page/Admin/Settings";
import WebsiteSettings from "../page/Admin/WebsiteSettings";
import RequirePermission from "../component/Admin/RequirePermission";
import { UNAUTH_ADMIN_REDIRECT } from "../helper/authPaths";

const AdminLayout = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showSidebar, setShowSidebar] = useState('-left-72');
    const [hasChecked, setHasChecked] = useState(false);
    const hasCheckedRef = useRef(false);

    const refreshAuth = useCallback(async (silent = true) => {
        try {
            const response = await axios.post('api/auth/verify', {}, {
                withCredentials: true
            });

            if (!response.data || !response.data.id) {
                setUser(null);
                return;
            }

            const authUser = response.data;
            if (authUser?.role === 'admin' || authUser?.role === 'operator') {
                setUser(authUser);
            } else {
                setUser(null);
            }
        } catch (error) {
            if (!silent) {
                setUser(null);
            }
        } finally {
            setIsLoading(false);
            setHasChecked(true);
            hasCheckedRef.current = true;
        }
    }, []);

    // Initial auth check
    useEffect(() => {
        refreshAuth(false);
    }, [refreshAuth]);

    // Refresh on focus / after menu-user save / periodic (JWT already reloads journalIds per API call)
    useEffect(() => {
        const onFocus = () => {
            if (hasCheckedRef.current) refreshAuth(true);
        };
        const onAuthRefresh = () => {
            if (hasCheckedRef.current) refreshAuth(true);
        };
        window.addEventListener('focus', onFocus);
        window.addEventListener('auth:refresh', onAuthRefresh);
        const interval = setInterval(() => {
            if (hasCheckedRef.current && document.visibilityState === 'visible') {
                refreshAuth(true);
            }
        }, 60000);

        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('auth:refresh', onAuthRefresh);
            clearInterval(interval);
        };
    }, [refreshAuth]);

    // Show loading state while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-canvas dark:bg-carbon">
                <div className="text-center">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-hairline border-t-primary"></div>
                    <p className="mt-4 text-sm text-muted">Loading...</p>
                </div>
            </div>
        );
    }

    // Belum login: jangan buka form login dari /admin (path disembunyikan)
    if (hasChecked && !isLoading && (!user || !user.role ||
        (user.role !== 'admin' && user.role !== 'operator'))) {
        return <Navigate to={UNAUTH_ADMIN_REDIRECT} replace />;
    }

    const data = {
        user: {
            id: user?.id,
            username: user?.username,
            nama: user?.nama,
            email: user?.email,
            role: user?.role,
            journalIds: user?.journalIds || [],
            permissions: user?.permissions || {},
        },
        token: null, // Token is in httpOnly cookie, not accessible from frontend
        refreshAuth: () => refreshAuth(true),
    }
    const permissions = data.user.permissions;
    return (
        <>
            <div className="min-h-screen bg-surface-soft dark:bg-carbon">
                <SidebarAdmin
                    data={data}
                    isCollapsed={isCollapsed}
                    setIsCollapsed={setIsCollapsed}
                    showSidebar={showSidebar}
                    setShowSidebar={setShowSidebar}
                />
                <div className={`transition-all duration-300 ${isCollapsed ? 'md:ml-[72px]' : 'md:ml-[260px]'}`}>
                    <div
                        className={`fixed top-0 right-0 z-40 left-0 transition-all duration-300 ${isCollapsed ? 'md:left-[72px]' : 'md:left-[260px]'}`}
                    >
                        <NavbarAdmin
                            showSidebar={showSidebar}
                            setShowSidebar={setShowSidebar}
                            isCollapsed={isCollapsed}
                            setIsCollapsed={setIsCollapsed}
                            data={data}
                        />
                    </div>
                    <main className="overflow-x-hidden pt-14 text-ink dark:text-white/90">
                        <div className="px-3 py-3 sm:px-4 sm:py-4 md:px-8 md:py-6">
                            <Routes>
                                <Route path="/" element={<DashboardAdmin data={data} />} />
                                <Route path="/dashboard" element={
                                    <RequirePermission permissions={permissions} menuKey="dashboard">
                                        <DashboardAdmin data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/loa" element={
                                    <RequirePermission permissions={permissions} menuKey="loa">
                                        <Loa data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/loa/create" element={
                                    <RequirePermission permissions={permissions} menuKey="loa" action="create">
                                        <LoaForm data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/loa/:loaId/edit" element={
                                    <RequirePermission permissions={permissions} menuKey="loa" action="edit">
                                        <LoaForm data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/loa/:loaId" element={
                                    <RequirePermission permissions={permissions} menuKey="loa">
                                        <LoaDetail data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/journal" element={
                                    <RequirePermission permissions={permissions} menuKey="journal">
                                        <Journal data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/journal/:journalId/issues" element={
                                    <RequirePermission permissions={permissions} menuKey="journal">
                                        <Issues data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/users" element={
                                    <RequirePermission permissions={permissions} menuKey="users">
                                        <Users data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/users/create" element={
                                    <RequirePermission permissions={permissions} menuKey="users" action="create">
                                        <UserForm data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/users/:userId/edit" element={
                                    <RequirePermission permissions={permissions} menuKey="users" action="edit">
                                        <UserForm data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/payment" element={
                                    <RequirePermission permissions={permissions} menuKey="payment">
                                        <Payment data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/payment/:trxID" element={
                                    <RequirePermission permissions={permissions} menuKey="payment">
                                        <PaymentDetail data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/payment/bill/:trxID" element={
                                    <RequirePermission permissions={permissions} menuKey="payment">
                                        <PaymentBill data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/payment/invoice/:trxID" element={
                                    <RequirePermission permissions={permissions} menuKey="payment">
                                        <PaymentInvoice data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/payment-method" element={
                                    <RequirePermission permissions={permissions} menuKey="payment_method">
                                        <PaymentMethod data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/menus" element={
                                    <RequirePermission permissions={permissions} menuKey="menu_manage">
                                        <MenuManage data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/menu-access" element={
                                    <RequirePermission permissions={permissions} menuKey="menu_access">
                                        <MenuAccess data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/website-settings" element={
                                    <RequirePermission permissions={permissions} menuKey="payment" action="edit">
                                        <WebsiteSettings data={data} />
                                    </RequirePermission>
                                } />
                                <Route path="/settings" element={<Settings data={data} />} />
                            </Routes>
                        </div>
                    </main>
                </div>
            </div>
        </>
    );
}

export default AdminLayout;
